import { useSnackbar } from 'app/hooks/useSnackbar';
import { IApplicationCustomVariablesValues } from 'app/interfaces/customVariablesInterfaces';
import { TApplicationSignatureParsed } from 'modules/products/pages/ApplicationDetails/components/ApplicationDetailsContent/ApplicationDetailsContent';
import { TSubscriber } from 'modules/products/pages/ApplicationRequest/SubscribersStep/SubscribersAddNewSideSheet';
import { useState } from 'react';
import {
  TUnicoDocumentsResponse,
  SignatureProvider,
} from 'modules/products/pages/interfaces';
import {
  ApplicationsHttpService,
  SignatureAuthenticationMode,
  SignatureUnicoAuthenticationMode,
  TApplicationParametersDataRequest,
  TApplicationSplitBeneficiaryAccount,
} from '../api/applicationsHttpService';
import { useAppContext } from 'app/context/AppContextProvider';
import { cacheService } from 'app/utils/cacheService';
import { TBeneficiaryAccount } from 'modules/products/interfaces/beneficiaryAccount';
import { TQualificationStatusOption } from 'modules/products/pages/interfaces';
import {
  TCustodian,
  TCustodianRequest,
} from 'modules/products/interfaces/custodian';
import { ValidateBilletsResponse } from 'modules/products/interfaces/billets';
import { ValidateEmvResponse } from 'modules/products/interfaces/qrCode';
import {
  ESplitPaymentAmountType,
  ESplitPaymentType,
  SplitPayment,
} from 'modules/products/interfaces/splitPayment';
import {
  TSimulatorV2Request,
  TSimulatorV2Response,
} from 'app/services/simulatorHttpServiceV2';
import {
  ISimulationResponse,
  PreFixedTypes,
} from 'modules/products/pages/ApplicationRequest/SimulationV2Step/CreditSimulatorV2/types';
import {
  ConsigneeAuthorizationPayload,
  ConsigneeStatus,
  ConsigneeOrganType,
} from 'modules/products/pages/ApplicationRequest/ConsignedStep/interfaces';
import { IPixKey } from 'modules/customer/context/CustomerProvider/customer.interfaces';
import { IList, ILoadingsInitialState } from './interfaces';
import { isAxiosError } from 'axios';

type TStatusDefinitions = {
  application_approval?: TQualificationStatusOption[];
  cession_approval?: TQualificationStatusOption[];
  employer_onboarding?: TQualificationStatusOption[];
  borrower_onboarding?: TQualificationStatusOption[];
};

export enum EPaymentMethod {
  BOLETO = 'BOLETO',
  PIX = 'PIX',
  TED = 'TED',
  BANK_ACCOUNT_DEDUCTION = 'BANK_ACCOUNT_DEDUCTION',
  PAYROLL_DEDUCTION = 'PAYROLL_DEDUCTION',
  RECEIVABLES = 'RECEIVABLES',
}

export const PaymentMethodDescription = {
  [EPaymentMethod.BOLETO]: 'Boleto',
  [EPaymentMethod.PIX]: 'Pix',
  [EPaymentMethod.TED]: 'TED',
  [EPaymentMethod.BANK_ACCOUNT_DEDUCTION]: 'Débito em conta',
  [EPaymentMethod.PAYROLL_DEDUCTION]: 'Consignação em folha de pagamento',
  [EPaymentMethod.RECEIVABLES]: 'Recebíveis',
};

export enum ESignatureCollectMethod {
  NONE = 'NONE',
  SMS = 'SMS',
  LINK = 'LINK',
  EMAIL = 'EMAIL',
  PHYSICAL = 'PHYSICAL',
  WHATSAPP = 'WHATSAPP',
}

export enum EIssuerDisbursementType {
  BENEFICIARY = 'BENEFICIARY',
  ORIGINATOR = 'ORIGINATOR',
}

export enum EApplicationStatus {
  AGREEMENT_RENDERING = 'AGREEMENT_RENDERING',
  CREATING_BENEFICIARY_ACCOUNT = 'CREATING_BENEFICIARY_ACCOUNT',
  PENDING_SIGNATURE = 'PENDING_SIGNATURE',
  PENDING_QUALIFICATION = 'PENDING_QUALIFICATION',
  PENDING_CESSION_QUALIFICATION = 'PENDING_CESSION_QUALIFICATION',
  PENDING_DISBURSEMENT = 'PENDING_DISBURSEMENT',
  PARTIAL_DISBURSEMENT = 'PARTIAL_DISBURSEMENT',
  DISBURSEMENT_ATTEMPT_FAILED = 'DISBURSEMENT_ATTEMPT_FAILED',
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  KYC_PROCESSING = 'KYC_PROCESSING',
  ISSUED = 'ISSUED',
  CANCELED = 'CANCELED',
  PENDING_RANDOM = 'PENDING_RANDOM',
  PENDING_CASHOUT = 'PENDING_CASHOUT',
  PENDING_INTEGRATION_LAQUS = 'PENDING_INTEGRATION_LAQUS',
  PENDING_CESSION = 'PENDING_CESSION',
  GUARANTEE_CANCEL_PROCESSING = 'GUARANTEE_CANCEL_PROCESSING',
  PENDING_GUARANTEE = 'PENDING_GUARANTEE',
  AWAITING_APPROVAL_DISBURSEMENT = 'AWAITING_APPROVAL_DISBURSEMENT',
  SIGNATURE_ERROR = 'SIGNATURE_ERROR',
  PENDING_ENTRY_PAYMENT = 'PENDING_ENTRY_PAYMENT',
  CREATING_ENTRY_PAYMENT = 'CREATING_ENTRY_PAYMENT',
  SCHEDULED_DISBURSEMENT = 'SCHEDULED_DISBURSEMENT',
  SENDING_CONSIGNEE_CONTRACT = 'SENDING_CONSIGNEE_CONTRACT',
  PENDING_CONTRACT_SUBMISSION = 'PENDING_CONTRACT_SUBMISSION',
}

export enum EApplicationStatusDescription {
  AWAITING_APPROVAL_DISBURSEMENT = 'Aguardando aprovação de desembolso',
  PENDING_ENTRY_PAYMENT = 'Aguardando pagamento de entrada',
  PENDING_CASHOUT = 'Aguardando pix',
  CANCELED = 'Cancelado',
  GUARANTEE_CANCEL_PROCESSING = 'Cancelando averbação',
  CREATING_BENEFICIARY_ACCOUNT = 'Criando conta do tomador',
  CREATING_ENTRY_PAYMENT = 'Criando pagamento de entrada',
  SCHEDULED_DISBURSEMENT = 'Desembolso agendado',
  PARTIAL_DISBURSEMENT = 'Desembolso parcial',
  ISSUED = 'Emitido',
  SENDING_CONSIGNEE_CONTRACT = 'Enviando contrato',
  PENDING_CONTRACT_SUBMISSION = 'Envio do contrato pendente',
  SIGNATURE_ERROR = 'Erro na assinatura',
  DISBURSEMENT_ATTEMPT_FAILED = 'Falha no pagamento',
  AGREEMENT_RENDERING = 'Gerando contrato',
  PENDING_SIGNATURE = 'Pendente de assinatura',
  PENDING_QUALIFICATION = 'Pendente de aprovação',
  PENDING_CESSION_QUALIFICATION = 'Pendente de aprovação da cessão',
  PENDING_GUARANTEE = 'Pendente de averbação',
  PENDING_CESSION = 'Pendente de cessão',
  PENDING_DISBURSEMENT = 'Pendente de desembolso',
  PENDING_INTEGRATION_LAQUS = 'Pendente de integração Laqus',
  PENDING_PAYMENT = 'Pendente de pagamento',
  KYC_PROCESSING = 'Pendente KYC',
  PENDING_RANDOM = 'Pendente (outros)',
}

export interface TQualificationRequestApplication {
  id: string;
  funding: Funding;
  employer: null;
  role: string;
  status: string;
  borrower: TQualificationRequestApplicationBorrower;
  application: TApplicationResponse;
  created_at: string;
  updated_at: string;
}

export interface ProductClass {
  id: string;
}

export type CustomVariables = unknown;

export interface Funding {
  id: string;
  legal_name: string;
}

export interface LoanDetails {
  total_processing_cost: number;
  annual_interest_rate: number;
  annual_effective_interest_rate: number;
  monthly_effective_interest_rate: number;
  total_amount_owed: number;
  disbursement_amount: number;
  financed_amount: number;
  iof_daily_rate: number;
  iof_amount: number;
  iof_base_rate: number;
  payment_amount: number;
  issue_date: string;
  schedule: Schedule[];
  last_payment_date: string;
}

export interface Schedule {
  payment: number;
  iof: number;
  balance: number;
  interest: number;
  principal: number;
  period: number;
  running_day: number;
  payment_date: string;
}

export interface TQualificationRequestApplicationBorrower {
  id: string;
  name: string;
}

export type ApplicationBeneficiaryAccountBaas = {
  account_number: string;
};

export type ApplicationBeneficiaryAccountExternalBankAccount = {
  bank_code: string;
  bank_account: string;
  bank_account_digit: string;
  bank_branch: string;
  bank_account_type: string;
  ispb_code: string;
};

export type ApplicationBeneficiaryAccountHolder = {
  name: string;
  taxpayer_id: string;
};

export type ApplicationBeneficiaryAccountPix = {
  key: string;
  key_type: string;
};

export type TApplicationBeneficiaryAccount = {
  beneficiary_account: ApplicationBeneficiaryAccount;
};

export interface ApplicationBeneficiaryAccount {
  baas: ApplicationBeneficiaryAccountBaas | null;
  external_bank_account: ApplicationBeneficiaryAccountExternalBankAccount | null;
  holder: ApplicationBeneficiaryAccountHolder;
  pix: IPixKey | null;
  registered_account_id: string | null;
}

export type PaymentOrderBilletInfo = {
  digitable: string;
  type: number;
  original_amount: number;
  amount: number;
  due_date: string;
  settle_date: string;
  assignor: string;
  payer: string;
  document_payer: string;
  recipient: string;
  document_recipient: string;
  validation_hash: string;
  discount_amount: string;
  fine_amount: string;
  identifier?: string;
  paid_at?: string;
  payment_authentication_code?: string;
};

export interface PaymentOrder {
  amount: number;
  beneficiary_account: ApplicationBeneficiaryAccount;
  beneficiary_type: string;
  id: string;
  status: string;
  type: string;
  billet_info?: PaymentOrderBilletInfo;
  allow_failure: unknown;
}

export enum EApplicationCessionStatus {
  NOT_CEDED = 'NOT_CEDED',
  ENDORSEMENT_RENDERING = 'ENDORSEMENT_RENDERING',
  ENDORSEMENT_RENDERED = 'ENDORSEMENT_RENDERED',
  ENDORSEMENT_SIGNED = 'ENDORSEMENT_SIGNED',
  CESSION_SIGNED = 'CESSION_SIGNED',
  ENDORSED = 'ENDORSED',
  CEDED = 'CEDED',
  WAITING_ENDORSEMENT_RENDERING = 'WAITING_ENDORSEMENT_RENDERING',
  SECURITIZATION_ENDORSEMENT_SIGNED = 'SECURITIZATION_ENDORSEMENT_SIGNED',
  CESSION_RENDERING = 'CESSION_RENDERING',
  CESSION_SENT_TO_SIGN = 'CESSION_SENT_TO_SIGN',
  SECURITIZATION_ENDORSEMENT_RENDERING = 'SECURITIZATION_ENDORSEMENT_RENDERING',
  SECURITIZATION_ENDORSEMENT_RENDERED = 'SECURITIZATION_ENDORSEMENT_RENDERED',
}

export type TApplicationResponse = {
  id: string;
  product: { id: string };
  borrower: { id: string };
  requested_amount: number;
  interest_rate: number;
  tac_amount: number;
  finance_fee: number;
  num_payments: number;
  first_payment_date: string;
  disbursement_date: string;
  issue_date: string;
  status: string;
  status_description?: string;
  base_premium_amount?: number;
  issue_value?: number;
  additional_premium_amount?: number;
  originator?: {
    id: string;
    legal_name: string;
  };
  managed_account_for_issuer_disbursement?: {
    product_configuration: null;
    baas?: {
      account_number: string;
      pix_key: string;
      holder_type: string;
    } | null;
  } | null;
  custodian: TCustodianRequest | null;
  ipoc_number: string | null;
  additional_installment_description: string | null;
  additional_installment_fee: number | null;
  split_beneficiary_account: TApplicationSplitBeneficiaryAccount | null;
  loan_details: {
    total_processing_cost: number;
    annual_interest_rate: number;
    annual_effective_interest_rate: number;
    monthly_effective_interest_rate: number;
    total_amount_owed: number;
    disbursement_amount: number;
    financed_amount: number;
    iof_daily_rate: number;
    iof_amount: number;
    iof_base_rate: number;
    payment_amount: number;
    issue_date: string;
    schedule: Array<{
      payment: number;
      iof: number;
      balance: number;
      interest: number;
      principal: number;
      period: number;
      running_day: number;
      payment_date: string;
      additional_values: number | null;
      present_value: number | null;
      status: string | null;
      late_day: number | null;
      paid_value: number | null;
    }>;
    last_payment_date: string;
  };
  custom_variables: { [key: string]: string | null | undefined };
  canceled_at: string | null;
  sequential_id: number;
  beneficiary_account?:
    | (TBeneficiaryAccount & { registered_account_id: string })
    | null;
  funding?: {
    id?: string;
    legal_name?: string;
    alias?: string;
  };
  payment_method: EPaymentMethod;
  backoffice_import_status: string;
  guarantors: Array<{ guarantor_id: string; type: 'PERSON' | 'BUSINESS' }>;
  co_signers: Array<{ co_signer_id: string; type: 'PERSON' | 'BUSINESS' }>;
  co_debtors: Array<{ co_debtor_id: string; type: 'PERSON' | 'BUSINESS' }>;
  investors: Array<{ investor_id: string; type: 'PERSON' | 'BUSINESS' }>;
  sureties: Array<{ surety_id: string; type: 'PERSON' | 'BUSINESS' }>;
  collateral_providers: Array<{
    collateral_provider_id: string;
    type: 'PERSON' | 'BUSINESS';
  }>;
  representatives: Array<{
    representative_id: string;
    type: 'PERSON' | 'BUSINESS';
  }>;

  signature_collect_method: ESignatureCollectMethod;
  signature_provider?: SignatureProvider;
  issuer_disbursement_type: EIssuerDisbursementType;
  disbursement?: {
    processed_at: string;
    receipt: string;
    requested_at: string;
  };
  signature_collect_options: {
    require_self_photo: boolean;
    require_document_photo: boolean;
    require_selfie_validation: boolean;
  };
  loan: null;
  created_at: string;
  payment_orders: PaymentOrder[];
  cession_status: EApplicationCessionStatus;
};

const useApplicationService = () => {
  // Variables
  const loadingsInitialState: ILoadingsInitialState = {
    isApplicationLoading: false,
    isBasePremiumAmountLoading: false,
    isCancelApplicationLoading: false,
    isCommercialPaperStatusUpdateLoading: false,
    isConsignedLoading: false,
    isSubmitConsigneeLoading: false,
    isDownloadAgreementLoading: false,
    isDownloadSignedAgreementLoading: false,
    isDownloadSignedEndorsementLoading: false,
    isDownloadSignedSecuritizationEndorsementLoading: false,
    isDownloadUnicoDocumentsLoading: false,
    isQualificationStatusOptionsLoading: false,
    isResendSignatureLoading: false,
    isUpdatePaymentOrderLoading: false,
    isUploadCustomCcbTemplateLoading: false,
  };

  // Hooks
  const { feature_flags, is_issuer_app } = useAppContext();
  const { showSnackbar } = useSnackbar();

  // States
  const [loadings, setLoadings] = useState(loadingsInitialState);
  const [productType, setProductType] = useState<ConsigneeOrganType>();
  const [unicoDocuments, setUnicoDocuments] =
    useState<TUnicoDocumentsResponse | null>(null);

  // Handlers
  const resendSignature = async (
    applicationId: string,
    originatorId?: string,
  ): Promise<boolean> => {
    try {
      setLoadings((prev) => ({ ...prev, isResendSignatureLoading: true }));
      const applicationService = new ApplicationsHttpService();
      if (originatorId) {
        applicationService.setOriginatorId(originatorId);
      }
      const res = await applicationService.resendSignature(applicationId);
      return res.data;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro resendSignature:', error);
    } finally {
      setLoadings((prev) => ({ ...prev, isResendSignatureLoading: false }));
    }
    return false;
  };

  const createApplication = async (
    productId: string,
    borrowerId: string,
    fundingId: string,
    customVariables: IApplicationCustomVariablesValues[] | null,
    subscribers: TSubscriber[] | null,
    signatureType: string,
    beneficiaryAccount: TBeneficiaryAccount | null,
    custodian: TCustodian | null,
    billetValidationResponse: ValidateBilletsResponse | null,
    qrcodeInfo: ValidateEmvResponse | null,
    paymentMethod?: 'PIX' | 'TED',
    simulationV2?: TSimulatorV2Request | null,
    signatureProvider: SignatureProvider = SignatureProvider.CLICKSIGN,
    signatureMethod:
      | SignatureAuthenticationMode
      | SignatureUnicoAuthenticationMode = SignatureAuthenticationMode.EMAIL,
    consignedId?: string,
    splitPayments: SplitPayment[] | null = null,
    financialIndicatorCobransaas?: ISimulationResponse,
    simulationV2Response: TSimulatorV2Response | null = null,
    customCcbTemplateId?: string | null,
    productOrganType?: string,
    productMultipleDisbursementsDates?: boolean,
  ): Promise<TApplicationResponse | null> => {
    const custodianParsed: TCustodianRequest | null = custodian
      ? {
          foundation_date: custodian?.foundation_date,
          email_address: custodian?.email_address?.trim(),
          legal_name: custodian?.legal_name?.trim(),
          phone: {
            area_code: custodian?.phone.number.replace(/\D+/g, '').slice(0, 2),
            country_code: custodian?.phone?.country_code || '55',
            number: custodian?.phone.number?.replace(/\D+/g, '').slice(2),
          },
          taxpayer_id: custodian?.taxpayer_id?.replace(/\D+/g, ''),
          address: {
            city: custodian?.address?.city.value?.trim(),
            state_code: custodian?.address?.state_code?.value,
            country_code: custodian?.address?.country_code?.value,
            district: custodian?.address?.district?.trim(),
            extra_info: custodian?.address?.extra_info?.trim(),
            postal_code: custodian?.address?.postal_code?.replace(/\D+/g, ''),
            street_name: custodian?.address?.street_name?.trim(),
            street_number: custodian?.address?.street_number?.trim(),
          },
        }
      : null;

    const beneficiaryAccountForSplit = splitPayments?.find(
      (splitPayment) => splitPayment.type === ESplitPaymentType.BENEFICIARY,
    ) as SplitPayment;
    const splitPaymentBeneficiary:
      | TApplicationSplitBeneficiaryAccount
      | undefined = beneficiaryAccountForSplit
      ? {
          external_bank_account:
            beneficiaryAccountForSplit.external_bank_account,
          holder: {
            name: beneficiaryAccountForSplit.name,
            taxpayer_id: beneficiaryAccountForSplit.taxpayer_id,
          },
          pix:
            beneficiaryAccountForSplit.pix?.key &&
            beneficiaryAccountForSplit.pix.key.length > 0
              ? beneficiaryAccountForSplit.pix
              : undefined,
          amount:
            beneficiaryAccountForSplit.amount_type ===
            ESplitPaymentAmountType.MONEY
              ? beneficiaryAccountForSplit.amount
              : undefined,
          percentage:
            beneficiaryAccountForSplit.amount_type ===
            ESplitPaymentAmountType.PERCENT
              ? beneficiaryAccountForSplit.amount
              : undefined,
        }
      : undefined;
    const billets_info =
      billetValidationResponse?.billets_info
        ?.filter((billet) => billet.succeeded)
        ?.map((billetInfo) => billetInfo.result) ?? null;

    try {
      setLoadings((prev) => ({ ...prev, isApplicationLoading: true }));

      const getApplicationPayload = (): TApplicationParametersDataRequest => {
        const payload = {
          borrowerId,
          productId,
          split_beneficiary_account: splitPaymentBeneficiary,
          custodian: custodianParsed,
          beneficiaryAccountId: beneficiaryAccount?.id,
          customVariables,
          subscribers,
          signatureType,
          fundingId,
          signatureProvider,
          paymentMethod,
          billets_info,
          qrcode_info: qrcodeInfo,
          signatureAuthenticationOptions: {
            mode: signatureMethod,
          },
          signatureCollectOptions: {
            requireSelfPhoto: feature_flags?.PORTAL_SIGNATURE_REQUIRE_SELFIE
              ? feature_flags?.PORTAL_SIGNATURE_REQUIRE_SELFIE?.isActive
              : feature_flags?.feature_flags?.signature_collect_options
                  ?.require_self_photo
              ? feature_flags?.feature_flags?.signature_collect_options
                  ?.require_self_photo
              : false,
            requireSelfieValidation:
              feature_flags?.PORTAL_SIGNATURE_REQUIRE_FACEMATCH
                ? feature_flags?.PORTAL_SIGNATURE_REQUIRE_FACEMATCH?.isActive
                : false,
          },
          balance_check_id: consignedId,
          financialIndicatorCobransaas,
          amortizationType: simulationV2Response?.amortization_type, // SAC, FLEXIBLE, PRICE ...
          amortizationFrequency: simulationV2Response?.amortization_frequency,
          gracePeriodAmortization:
            simulationV2Response?.grace_period_amortization || [],
          interestPreType:
            simulationV2Response?.interest_pre_type || PreFixedTypes.BASE_30,
          interestRateFrequency: simulationV2Response?.interest_rate_frequency,
          customCcbTemplateId: customCcbTemplateId || undefined,
          simulation_id: simulationV2Response?.id || undefined,
        };

        if (
          simulationV2 &&
          simulationV2Response &&
          (typeof simulationV2?.requestedAmount === 'number' ||
            typeof simulationV2Response?.requested_amount === 'number')
        ) {
          // Para produtos CT com múltiplas datas de desembolso, usa a data selecionada no seletor (disbursement_date da resposta)
          // Caso contrário, usa a data do request (comportamento padrão)
          const disbursementDateToUse =
            productOrganType === ConsigneeOrganType.CT &&
            productMultipleDisbursementsDates &&
            simulationV2Response?.disbursement_date
              ? (() => {
                  // Info: Não usar new Date() para evitar deslocamento de timezone que pode resultar em 1 dia a menos
                  const dateString = simulationV2Response.disbursement_date;
                  if (dateString.includes('T')) {
                    return dateString.split('T')[0];
                  }
                  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                    return dateString;
                  }
                  return dateString.substring(0, 10);
                })()
              : simulationV2.disbursementDate;

          return {
            amount:
              simulationV2.requestedAmount ||
              simulationV2Response.requested_amount,
            numPayments: simulationV2.numPayments,
            interestRate: simulationV2.interestRate,
            tacAmount: simulationV2.tacAmount,
            tacRate: simulationV2.tacRate,
            financeFee: simulationV2.financeFee,
            insurance_amount: simulationV2.insurance_amount,
            firstPaymentDate: simulationV2.firstPaymentDate,
            disbursementDate: disbursementDateToUse,
            additional_installment_description:
              simulationV2.additionalInstallmentDescription,
            additional_installment_fee: simulationV2.additionalInstallmentFee,
            ...payload,
          };
        }

        throw new Error('Invalid simulation data');
      };

      const applicationService = new ApplicationsHttpService();
      const res = await applicationService.createApplication(
        getApplicationPayload(),
      );
      return res.data;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro getApplicationPayload:', error);
    } finally {
      setLoadings((prev) => ({ ...prev, isApplicationLoading: false }));
    }

    return null;
  };

  const getBasePremiumAmount = async (
    financedAmount: number,
    fundingId: string,
  ): Promise<{ amount: number }> => {
    try {
      setLoadings((prev) => ({ ...prev, isGetBasePremiumAmountLoading: true }));
      const applicationService = new ApplicationsHttpService();
      applicationService.setHeader({
        handled: true,
      });
      const res = await applicationService.getBasePremiumAmount(
        financedAmount,
        fundingId,
      );
      return res.data;
    } catch (error) {
      console.error('Erro getBasePremiumAmount:', error);
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isGetBasePremiumAmountLoading: false,
      }));
    }
    return { amount: 0 };
  };

  const getApplication = async (
    applicationId: string,
  ): Promise<TApplicationResponse | null> => {
    try {
      setLoadings((prev) => ({ ...prev, isApplicationLoading: true }));
      const applicationService = new ApplicationsHttpService();
      applicationService.setHeader({
        handled: true,
      });
      const res = await applicationService.getApplication(
        applicationId,
        is_issuer_app,
      );
      return res.data;
    } catch (error) {
      console.error('Erro getApplication:', error);
    } finally {
      setLoadings((prev) => ({ ...prev, isApplicationLoading: false }));
    }
    return null;
  };

  const getApplications = async (
    page: number,
    size?: number,
    parameters?: { [key: string]: string | null | undefined },
    filterStatusSelect?: string[],
    filterIds?: string[],
  ): Promise<IList<TApplicationResponse> | null> => {
    try {
      setLoadings((prev) => ({ ...prev, isGetApplicationsLoading: true }));
      const applicationService = new ApplicationsHttpService();
      const res = await applicationService.getApplications(
        page,
        size,
        parameters,
        filterStatusSelect,
        filterIds,
        is_issuer_app,
      );
      return res.data;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro getApplications:', error);
    } finally {
      setLoadings((prev) => ({ ...prev, isGetApplicationsLoading: false }));
    }
    return null;
  };

  const cancelApplication = async (
    applicationId: string,
    originatorId?: string,
  ): Promise<boolean> => {
    try {
      setLoadings((prev) => ({ ...prev, isCancelApplicationLoading: true }));
      const applicationService = new ApplicationsHttpService();
      if (originatorId) {
        applicationService.setOriginatorId(originatorId);
      }
      const res = await applicationService.cancelApplication(applicationId);
      return res.data;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro cancelApplication:', error);
    } finally {
      setLoadings((prev) => ({ ...prev, isCancelApplicationLoading: false }));
    }
    return false;
  };

  const submitConsignee = async (applicationId: string): Promise<boolean> => {
    try {
      setLoadings((prev) => ({ ...prev, isSubmitConsigneeLoading: true }));
      const applicationService = new ApplicationsHttpService();
      const res = await applicationService.submitConsignee(applicationId);

      if (res.status === 200) {
        await getApplication(applicationId);
        return true;
      }

      return false;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro submitConsignee:', error);
      return false;
    } finally {
      setLoadings((prev) => ({ ...prev, isSubmitConsigneeLoading: false }));
    }
  };

  const getQualificationStatusOptions = async (
    productId: string,
    originator?: string,
    applicationStatus?: EApplicationStatus,
  ) => {
    const cacheKey = `status-definitions:${productId}:${
      originator || 'default'
    }`;
    const cached = cacheService.get<TStatusDefinitions>(cacheKey);

    if (cached) {
      if (
        applicationStatus === EApplicationStatus.PENDING_CESSION_QUALIFICATION
      ) {
        return Array.isArray(cached?.cession_approval)
          ? cached.cession_approval
          : [];
      }
      return Array.isArray(cached?.application_approval)
        ? cached.application_approval
        : [];
    }

    try {
      setLoadings((prev) => ({
        ...prev,
        isGetQualificationStatusOptionsLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      if (originator) {
        applicationsHttpService.setOriginatorId(originator);
      }
      const result = await applicationsHttpService.getApplicationStatusOptions(
        productId,
      );

      if (result.data) {
        cacheService.set(cacheKey, result.data, 15 * 60 * 1000);
      }

      if (
        applicationStatus === EApplicationStatus.PENDING_CESSION_QUALIFICATION
      ) {
        return Array.isArray(result.data?.cession_approval)
          ? result.data?.cession_approval
          : [];
      }
      return Array.isArray(result.data?.application_approval)
        ? result.data?.application_approval
        : [];
    } catch (error) {
      console.error('Erro na chamada getQualificationStatusOptions', error);
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isGetQualificationStatusOptionsLoading: false,
      }));
    }
  };

  const getQualificationRequestApplication = async (
    productId: string,
    page: number,
    borrowerId?: string,
    applicationId?: string,
    originatorId?: string,
    applicationStatus?: EApplicationStatus,
  ): Promise<IList<TQualificationRequestApplication> | undefined> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isGetQualificationRequestApplicationLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      if (originatorId) {
        applicationsHttpService.setOriginatorId(originatorId);
      }

      const role =
        applicationStatus === EApplicationStatus.PENDING_CESSION_QUALIFICATION
          ? 'CESSION'
          : 'APPLICATION';
      const result =
        await applicationsHttpService.getQualificationRequestApplication(
          productId,
          page,
          borrowerId,
          applicationId,
          role,
        );
      return result;
    } catch (error) {
      console.error(
        'Erro na chamada getQualificationRequestApplication',
        error,
      );
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isGetQualificationRequestApplicationLoading: false,
      }));
    }
  };

  const getApplicationSignatures = async (
    applicationId: string,
  ): Promise<Array<TApplicationSignatureParsed> | null> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isGetApplicationSignaturesLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      const result = await applicationsHttpService.getApplicationSignatures(
        applicationId,
      );

      return result;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro na chamada getApplicationSignatures', error);
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isGetApplicationSignaturesLoading: false,
      }));
    }
    return null;
  };

  const sendConsigneeContract = async (
    applicationId: string,
  ): Promise<boolean> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isSendConsigneeContractLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      const result = await applicationsHttpService.sendConsigneeContract(
        applicationId,
      );

      if (result) {
        showSnackbar('Reenvio realizado.');
        return true;
      }
      return false;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro na chamada sendConsigneeContract', error);
      return false;
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isSendConsigneeContractLoading: false,
      }));
    }
  };

  const getApplicationAgreement = async (applicationId: string) => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isDownloadAgreementLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      applicationsHttpService.setRequestConfig({
        responseType: 'arraybuffer',
      });
      const result = await applicationsHttpService.getAgreementFile(
        applicationId,
      );

      return result;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro getApplicationAgreement:', error);
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isDownloadAgreementLoading: false,
      }));
    }
  };

  const getSignedApplicationAgreement = async (
    applicationId: string,
  ): Promise<{ url: string } | null> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isDownloadSignedAgreementLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      const result = await applicationsHttpService.getSignedAgreementFile(
        applicationId,
      );

      return result;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro na chamada getSignedApplicationAgreement', error);
      return null;
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isDownloadSignedAgreementLoading: false,
      }));
    }
  };

  const getSignedMarginConsentFile = async (
    taxpayer_id: string,
  ): Promise<string | null> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isConsignedLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      applicationsHttpService.setRequestConfig({
        responseType: 'arraybuffer',
      });
      applicationsHttpService.setHeader({
        handled: true,
      });
      const result = await applicationsHttpService.getSignedMarginConsentFile(
        taxpayer_id,
      );

      return result;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        showSnackbar(
          'O termo de consentimento não está disponível para download no momento. A indisponibilidade pode estar relacionada à forma de coleta da evidência (coleta externa) ou a uma falha temporária de armazenamento.',
        );
        return null;
      }
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('GetSignedMarginConsentFile', error);
      return null;
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isConsignedLoading: false,
      }));
    }
  };

  const getSignedEndorsementFile = async (
    applicationId: string,
  ): Promise<string | null> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isDownloadSignedEndorsementLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      applicationsHttpService.setRequestConfig({
        responseType: 'arraybuffer',
      });
      const result = await applicationsHttpService.getSignedEndorsementFile(
        applicationId,
      );

      return result;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro getSignedEndorsementFile:', error);
      return null;
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isDownloadSignedEndorsementLoading: false,
      }));
    }
  };

  const getSignedSecuritizationEndorsementFile = async (
    applicationId: string,
  ): Promise<string | null> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isDownloadSignedSecuritizationEndorsementLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      applicationsHttpService.setRequestConfig({
        responseType: 'arraybuffer',
      });
      const result =
        await applicationsHttpService.getSignedSecuritizationEndorsementFile(
          applicationId,
        );

      return result;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro getSignedSecuritizationEndorsementFile:', error);
      return null;
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isDownloadSignedSecuritizationEndorsementLoading: false,
      }));
    }
  };

  const updateApplicationPaymentOrder = async (
    applicationId: string,
    paymentOrderId: string,
    data: TApplicationBeneficiaryAccount,
  ) => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isUpdatePaymentOrderLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      const res = await applicationsHttpService.updateApplicationPaymentOrder(
        applicationId,
        paymentOrderId,
        data,
      );

      return res;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro updateApplicationPaymentOrder:', error);
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isUpdatePaymentOrderLoading: false,
      }));
    }
  };

  const createDisbursementAttempt = async (applicationId: string) => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isCreateDisbursementAttemptLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      const res = await applicationsHttpService.createDisbursementAttempt(
        applicationId,
      );

      return res;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro createDisbursementAttempt:', error);
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isCreateDisbursementAttemptLoading: false,
      }));
    }
  };

  const commercialPaperStatusUpdate = async (applicationId: string) => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isCommercialPaperStatusUpdateLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      const res = await applicationsHttpService.commercialPaperStatusUpdate(
        applicationId,
      );

      return res;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro commercialPaperStatusUpdate:', error);
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isCommercialPaperStatusUpdateLoading: false,
      }));
    }
  };

  const getConsigneeStatus = async (
    taxId: string,
    setConsignedError?: (error: string) => void,
  ): Promise<ConsigneeStatus | undefined> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isConsignedLoading: true,
      }));

      const applicationsHttpService = new ApplicationsHttpService();
      const statusResponse = await applicationsHttpService.getConsignedStatus(
        taxId,
      );

      if (statusResponse) {
        return statusResponse;
      }

      return;
    } catch (error: unknown) {
      console.error('Error getConsigneeStatus:', error);
      if (
        setConsignedError &&
        error &&
        typeof error === 'object' &&
        'response' in error
      ) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        if (axiosError.response?.data?.message) {
          setConsignedError(axiosError.response.data.message);
        }
      }
      return;
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isConsignedLoading: false,
      }));
    }
  };

  const sendConsigneeTermAuthorization = async (
    payload: ConsigneeAuthorizationPayload,
    setConsignedError?: (error: string) => void,
  ): Promise<boolean> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isConsignedLoading: true,
      }));

      const applicationsHttpService = new ApplicationsHttpService();
      const response =
        await applicationsHttpService.sendConsigneeTermAuthorization(payload);

      if (response) {
        return true;
      }

      return false;
    } catch (error: unknown) {
      console.error('Erro sendConsigneeTermAuthorization:', error);
      if (
        setConsignedError &&
        error &&
        typeof error === 'object' &&
        'response' in error
      ) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        if (axiosError.response?.data?.message) {
          setConsignedError(axiosError.response.data.message);
        }
      }
      return false;
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isConsignedLoading: false,
      }));
    }
  };

  const getConsigneeAuthorization = async (
    taxId: string,
    productId: string,
    setConsignedError?: (error: string) => void,
  ): Promise<string> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isConsignedLoading: true,
      }));

      const applicationsHttpService = new ApplicationsHttpService();
      const isAuthorized =
        await applicationsHttpService.getConsignedAuthorization({
          product: {
            id: productId,
          },
          taxpayer_id: taxId,
        });

      // HK aplicar tratativa dos status
      if (isAuthorized.status === 200) {
        return ConsigneeStatus.AUTHORIZED;
      }

      if (isAuthorized.status === 201) {
        return ConsigneeStatus.NOT_AUTHORIZED;
      }

      if (isAuthorized.status === 203) {
        return ConsigneeStatus.WAITING_SIGNATURE;
      }

      return '';
    } catch (error: unknown) {
      console.error('Error getConsigneeAuthorization:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { status?: number; data?: { message?: string } };
        };

        if (axiosError.response?.status === 429) {
          showSnackbar(
            'Serviço indisponível no momento, por favor tente novamente.',
            'alert',
          );
          return '';
        }

        if (setConsignedError && axiosError.response?.data?.message) {
          setConsignedError(axiosError.response.data.message);
        }
      }
      return '';
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isConsignedLoading: false,
      }));
    }
  };

  const getConsignedList = async (
    taxId: string,
    productId: string,
    setConsignedError?: (error: string) => void,
  ) => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isConsignedLoading: true,
      }));

      const applicationsHttpService = new ApplicationsHttpService();

      const result = await applicationsHttpService.getConsigned(
        taxId,
        productId,
      );

      if (result.status === 201) {
        showSnackbar('O CPF não está disponível para consulta neste produto.');
        return null;
      }

      if (result.status === 200) {
        return result.data;
      }
    } catch (error: unknown) {
      console.error('Error getConsignedList:', error);
      if (
        setConsignedError &&
        error &&
        typeof error === 'object' &&
        'response' in error
      ) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        if (axiosError.response?.data?.message) {
          setConsignedError(axiosError.response.data.message);
        }
      }
      return null;
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isConsignedLoading: false,
      }));
    }
  };

  const getConsigneeReceipt = async (
    applicationId: string,
  ): Promise<string | undefined> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isConsignedLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      applicationsHttpService.setRequestConfig({
        responseType: 'arraybuffer',
      });
      const result = await applicationsHttpService.getConsigneeReceipt(
        applicationId,
      );

      return result;

      // Não tratar com catch para não aparecer retorno na UI qdo não tiver o comprovante no S3.
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isConsignedLoading: false,
      }));
    }
  };

  const uploadCustomCcbTemplate = async (
    data: FormData,
  ): Promise<{ id: string } | null> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isUploadCustomCcbTemplateLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      const res = await applicationsHttpService.uploadCustomCcbTemplate(data);
      return res.data;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro uploadCustomCcbTemplate:', error);
      return null;
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isUploadCustomCcbTemplateLoading: false,
      }));
    }
  };

  const getUnicoDocuments = async (applicationId: string): Promise<boolean> => {
    try {
      setLoadings((prev) => ({
        ...prev,
        isDownloadUnicoDocumentsLoading: true,
      }));
      const applicationsHttpService = new ApplicationsHttpService();
      const res = await applicationsHttpService.getUnicoDocuments(
        applicationId,
      );

      let documentsData = null;

      if (res && typeof res === 'object') {
        // Verificar se tem CCB_ASSINADA
        if (res.CCB_ASSINADA && Array.isArray(res.CCB_ASSINADA)) {
          documentsData = res;
        }
        // Verificar se é um array direto
        else if (Array.isArray(res)) {
          documentsData = { CCB_ASSINADA: res };
        }
      }

      if (res.data && typeof res.data === 'object') {
        if (res.data.CCB_ASSINADA && Array.isArray(res.data.CCB_ASSINADA)) {
          documentsData = res.data;
        } else if (Array.isArray(res.data)) {
          documentsData = { CCB_ASSINADA: res.data };
        }
      }

      if (documentsData) {
        setUnicoDocuments(documentsData);
      } else {
        setUnicoDocuments(null);
      }
      return true;
    } catch (error) {
      showSnackbar('Ocorreu um erro. Tente novamente.');
      console.error('Erro getUnicoDocuments:', error);
      setUnicoDocuments(null);
      return false;
    } finally {
      setLoadings((prev) => ({
        ...prev,
        isDownloadUnicoDocumentsLoading: false,
      }));
    }
  };

  return {
    loadings,
    setLoadings,
    productType,
    setProductType,
    createApplication,
    getApplication,
    getApplications,
    cancelApplication,
    getQualificationStatusOptions,
    getQualificationRequestApplication,
    getApplicationSignatures,
    getApplicationAgreement,
    getSignedApplicationAgreement,
    getSignedMarginConsentFile,
    updateApplicationPaymentOrder,
    createDisbursementAttempt,
    getBasePremiumAmount,
    commercialPaperStatusUpdate,
    getSignedEndorsementFile,
    resendSignature,
    getSignedSecuritizationEndorsementFile,
    getConsignedList,
    uploadCustomCcbTemplate,
    getConsigneeAuthorization,
    getConsigneeStatus,
    sendConsigneeTermAuthorization,
    getConsigneeReceipt,
    sendConsigneeContract,
    submitConsignee,
    getUnicoDocuments,
    setUnicoDocuments,
    unicoDocuments,
  };
};

export default useApplicationService;
